import { cn } from '@h2hspark/ui';
import { Clock, MessageCircle, UserPlus } from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router-dom';

import type { MemberRelationship } from '../attendee-preview/types';
import { MESSAGE_ACTION_LABEL, threadPath } from '../messages/constants';

import { ConnectRequestDialog } from './connect-request-dialog';
import { CONNECTED_LABEL, CONNECT_LABEL, CONNECT_SENT_LABEL } from './constants';

/**
 * **Connect, as one control wherever it is offered** (`MEM-032` AC-2).
 *
 * AC-2 names three entry points — a match card, an attendee profile and the in-event roster — and
 * the 10 September Matches screen draws two of them side by side, so this is one component for the
 * same reason `SaveToMeetButton` is. It opens the composer that already exists (AC-3), pre-filled
 * by its caller from the context the request comes out of (AC-4).
 *
 * ## The state is the server's, since Sumit's ruling 29 of 2026-09-11
 *
 * It was `React.useState(false)`, and it was wrong twice: *"I connected to priya nair but it still
 * says connect"*, and *"after I send connect request and navigate away from screen and come back it
 * says connect again"*. A control that remembers only the last few seconds invites a member to send
 * the same person four requests and tells them nothing about somebody they have already met.
 *
 * So `relationship` arrives on the payload — on the match suggestion and on the attendee card
 * alike — and this renders it. The local `sent` flag is kept for exactly one job: the moment
 * between the composer's *Send* and the refetch landing, so the control does not sit on *Connect*
 * while the request is in flight. It is an **optimistic overlay on a served fact**, never the fact.
 *
 * ## The three states, and the fourth that is not here
 *
 * `connected`, `request-sent`, `none` — which are the three §3.8 and §3.9 draw, and which
 * `MEM-034` AC-5 bounds: the only actions on a match are Connect and Save to Meet, so a state with
 * a third control has nowhere to go. *They have asked me* is answered by the connection-requests
 * page, which the header card opens. `member-relationship-port.ts` on the API side carries the
 * whole argument, including why a **declined** request reads *Request sent* like a pending one.
 *
 * ## The two treatments the prototype draws
 *
 * `match` (§3.8) is a full-width pill under Save to Meet: outline-brand for Connect, the success
 * colours for both *Request sent* and *Connected*. `attendee` (§3.9) is the lower half of the
 * stacked pair at the right of a row: filled `--brand-primary` for Connect, the **warning** colours
 * for *Request sent* and the success colours for *Connected*. Both are `w-full` inside a column of
 * fixed width, so a state change never resizes anything (Sumit's ruling 28).
 *
 * `detail` (§2.16) is the right half of the pair at the foot of the attendee detail card — `flex:1`
 * of a two-button row at `--text-md` with 13px of padding: the **spark gradient** for Connect, the
 * warning colours for *Request sent* and the success colours for the third state.
 *
 * ## The third state reads *Message* and opens the thread (ruling 68)
 *
 * §2.16 draws it that way, and until 2026-09-12 this control said *Connected* instead, because
 * `MEM-054`'s thread is addressed by a **connection** reference and no attendee-facing payload
 * carried one. Sumit, asked: *"yes, wire up message and open the thread."* So the reference is
 * served beside the state it belongs to — `MemberRelationshipPort` decides `connected` from the
 * connection row and hands out the row's own handle — and the control is a `<Link>` to
 * `/app/messages/{connectionRef}` in that state, on **all three** surfaces.
 *
 * **It is Connections' own destination, not a second opener.** `connection-card.tsx` has linked
 * there since `MEM-054`, `threadPath` is the one place the address is written, and the thread that
 * opens is the §2.16 dialog on desktop and the full-screen view on a phone — the arrangement
 * `messages/thread-dialog.tsx` already owns. Nothing about messaging is built here; this control
 * only learned where it lives.
 *
 * **A link rather than a button, and the footprint does not move.** `w-full` (or `flex-1` on the
 * detail) with the same padding and type as the other two states, so ruling 28 holds: the row does
 * not resize when a request is accepted. The classes are computed once, below, for exactly that
 * reason.
 *
 * A `connected` state that arrives with **no** handle — which the API's
 * `connected-carries-a-thread.test.ts` says cannot happen — falls back to the old inert
 * *Connected*. A Message control that opened nothing would be worse than the accurate word.
 *
 * Renders nothing when the person did not resolve — there is no reference to address.
 */
export function ConnectButton({
  eventId,
  attendeeRef,
  person,
  prefilledMessage,
  relationship,
  connectionRef,
  variant,
}: {
  readonly eventId: string;
  readonly attendeeRef: string | null;
  readonly person: {
    readonly firstName: string;
    readonly role: string | null;
    readonly company: string | null;
  };
  /** `MEM-032` AC-4's match context, or `null` — an attendee card is not a match, and
   * pre-filling *"we matched"* there would be a claim the platform cannot support. */
  readonly prefilledMessage: string | null;
  /** The **served** state. `none` renders Connect; the caller does not decide. */
  readonly relationship: MemberRelationship;
  /**
   * `MEM-054`'s thread handle, served beside a `connected` relationship and `null` otherwise
   * (ruling 68). What *Message* opens.
   */
  readonly connectionRef: string | null;
  readonly variant: 'match' | 'attendee' | 'detail';
}): React.ReactElement | null {
  const [open, setOpen] = React.useState(false);
  /**
   * The optimistic half-second, and nothing more.
   *
   * Cleared whenever the server's own answer catches up, so a member who sends a request and then
   * has it accepted reads *Connected* rather than staying on this. It can only ever move the
   * control **forwards** — there is no path here that turns a served `connected` back into
   * `request-sent`.
   */
  const [justSent, setJustSent] = React.useState(false);
  React.useEffect(() => {
    if (relationship !== 'none') setJustSent(false);
  }, [relationship]);

  if (attendeeRef === null) return null;

  const state: MemberRelationship =
    relationship === 'none' && justSent ? 'request-sent' : relationship;
  const settled = state !== 'none';

  /* One class string for all three states, so a link and a button occupy the same box (ruling
     28) and the row does not resize when a request is accepted. */
  const shape = cn(
    'inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-pill border-[1.5px] font-brand font-bold disabled:cursor-default',
    variant === 'match'
      ? 'w-full p-2.5 text-sm'
      : variant === 'attendee'
        ? 'w-full px-3 py-2.5 text-sm'
        : 'flex-1 basis-0 p-3 text-md',
    state === 'connected'
      ? 'border-success bg-success-subtle text-success'
      : state === 'request-sent'
        ? variant === 'match'
          ? 'border-success bg-success-subtle text-success'
          : 'border-warning bg-warning-subtle text-warning'
        : variant === 'match'
          ? 'border-primary bg-card text-primary'
          : variant === 'attendee'
            ? 'border-transparent bg-primary text-primary-foreground'
            : 'border-transparent bg-spark text-white',
  );

  /*
    **Connected: the way into the conversation** (ruling 68). Connections' own destination —
    `threadPath`, the one place the address is written — so the thread a member opens from a match
    card is the thread the Messages page shows them, and the dialog-or-full-screen behaviour is
    `messages/thread-dialog.tsx`'s at both widths.
  */
  if (state === 'connected' && connectionRef !== null) {
    return (
      <Link
        to={threadPath(connectionRef)}
        aria-label={`${MESSAGE_ACTION_LABEL} ${person.firstName}`}
        className={shape}
      >
        <MessageCircle className="h-4 w-4" aria-hidden />
        {MESSAGE_ACTION_LABEL}
      </Link>
    );
  }

  return (
    <>
      <button
        type="button"
        disabled={settled}
        aria-label={state === 'connected' ? `${CONNECTED_LABEL} — ${person.firstName}` : undefined}
        onClick={() => {
          setOpen(true);
        }}
        className={shape}
      >
        {state === 'connected' ? (
          <MessageCircle className="h-4 w-4" aria-hidden />
        ) : state === 'request-sent' ? (
          <Clock className="h-4 w-4" aria-hidden />
        ) : (
          <UserPlus className="h-4 w-4" aria-hidden />
        )}
        {state === 'connected'
          ? CONNECTED_LABEL
          : state === 'request-sent'
            ? CONNECT_SENT_LABEL
            : CONNECT_LABEL}
      </button>
      <ConnectRequestDialog
        open={open}
        person={person}
        eventId={eventId}
        attendeeRef={attendeeRef}
        prefilledMessage={prefilledMessage}
        onOpenChange={setOpen}
        onSent={() => {
          setJustSent(true);
        }}
      />
    </>
  );
}
